import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getAnalyticsDashboardRequest, getTATRequest, getWorkloadRequest, getRejectionRateRequest, getTestVolumeRequest, getQCPerformanceRequest } from '../../../../redux/modules/laboratory/labAnalytics/slice';
import { Box, Typography, Grid, Paper, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TurnaroundOverview from './overview';

const StatCard = ({ title, value, icon: Icon, color = 'primary' }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: `${color}.light`, color: `${color}.main`
            }}>
                <Icon />
            </Box>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{title}</Typography>
            </Box>
        </CardContent>
    </Card>
);

function LabAnalyticsDashboard() {
    const dispatch = useDispatch();
    const { dashboardRes, tatRes, workloadRes, rejectionRateRes, testVolumeRes, qcPerformanceRes, loading } = useSelector(state => state.labAnalytics);
    const [days, setDays] = useState(30);
    // The span getTAT measures, named the way the order Timeline names it.
    const tatFromCollection = useSelector(state => !!state.hospitalConfig?.lisFlow?.collectionVerification);
    const tatToRelease = useSelector(state => !!state.hospitalConfig?.features?.['lis.tatToRelease']);
    const tatSpan = `${tatFromCollection ? 'Collection' : 'Accession'} to ${tatToRelease ? 'released' : 'verified'}`;

    useEffect(() => {
        dispatch(setMainHeading("Lab Analytics"));
        dispatch(getAnalyticsDashboardRequest());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getTATRequest({ days }));
        dispatch(getWorkloadRequest({ days }));
        dispatch(getRejectionRateRequest({ days }));
        dispatch(getTestVolumeRequest({ days }));
        dispatch(getQCPerformanceRequest({ days }));
    }, [dispatch, days]);

    const dashboard = dashboardRes?.data || {};
    const tat = tatRes?.data || {};
    const workload = workloadRes?.data || {};
    const rejection = rejectionRateRes?.data || {};
    const volume = testVolumeRes?.data || {};
    const qc = qcPerformanceRes?.data || {};

    const formatMinutes = (min) => {
        if (!min && min !== 0) return '-';
        // Round first, then split, so it never reads "18h 60m".
        const total = Math.round(min);
        if (total < 60) return `${total}m`;
        return `${Math.floor(total / 60)}h ${total % 60}m`;
    };

    return (
        <Box>
            {/* Period selector */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Today's snapshot + historical trends
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Period</InputLabel>
                    <Select value={days} label="Period" onChange={e => setDays(e.target.value)}>
                        <MenuItem value={7}>Last 7 days</MenuItem>
                        <MenuItem value={14}>Last 14 days</MenuItem>
                        <MenuItem value={30}>Last 30 days</MenuItem>
                        <MenuItem value={90}>Last 90 days</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Today's Stats */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard title="Orders Today" value={dashboard.totalOrdersToday ?? '-'} icon={AssignmentOutlinedIcon} color="primary" />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard title="Pending Results" value={dashboard.pendingResults ?? '-'} icon={PendingActionsIcon} color="warning" />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard title="Awaiting Auth" value={dashboard.awaitingAuthorization ?? '-'} icon={VerifiedOutlinedIcon} color="info" />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard title="Completed Today" value={dashboard.completedToday ?? '-'} icon={CheckCircleOutlineIcon} color="success" />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard title="STAT Orders" value={dashboard.statOrdersToday ?? '-'} icon={PriorityHighIcon} color="error" />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard title="Rejection Rate" value={`${rejection.rejectionRate || '0.00'}%`} icon={AccessTimeIcon} color="warning" />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* TAT by Priority */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Turnaround Time by Priority</Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>{tatSpan}</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Priority</TableCell>
                                        <TableCell align="right">Avg TAT</TableCell>
                                        <TableCell align="right">Min</TableCell>
                                        <TableCell align="right">Max</TableCell>
                                        <TableCell align="right">Orders</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(tat.tatByPriority || []).map(t => (
                                        <TableRow key={t._id}>
                                            <TableCell>
                                                <Chip label={t._id} size="small" color={t._id === 'stat' ? 'error' : t._id === 'urgent' ? 'warning' : 'default'} />
                                            </TableCell>
                                            <TableCell align="right">{formatMinutes(t.avgTotalTAT)}</TableCell>
                                            <TableCell align="right">{formatMinutes(t.minTotalTAT)}</TableCell>
                                            <TableCell align="right">{formatMinutes(t.maxTotalTAT)}</TableCell>
                                            <TableCell align="right">{t.count}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!tat.tatByPriority || tat.tatByPriority.length === 0) && (
                                        <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>No completed orders in period</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Test Volume */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Top Tests by Volume</Typography>
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Test</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell align="right">Count</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(volume.byTest || []).map((t, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{t.testName || '-'}</TableCell>
                                            <TableCell>{t.testCode || '-'}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>{t.count}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!volume.byTest || volume.byTest.length === 0) && (
                                        <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>No test data</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Workload by Technician */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Results by Technician</Typography>
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Technician</TableCell>
                                        <TableCell align="right">Results Entered</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(workload.byTechnician || []).map((t, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{t.name || 'Unknown'}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>{t.count}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!workload.byTechnician || workload.byTechnician.length === 0) && (
                                        <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>No workload data</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* QC Performance */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>QC Performance</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{qc.total || 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">Total QC Runs</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: qc.violations > 0 ? 'warning.main' : 'success.main' }}>{qc.violations || 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">Violations</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>{qc.accepted || 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">Accepted</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>{qc.rejected || 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">Rejected</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            Violation Rate: {qc.violationRate || '0.00'}%
                        </Typography>
                    </Paper>
                </Grid>

                {/* Rejection Details */}
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Specimen Rejection Analysis</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{rejection.total || 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">Total Specimens</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>{rejection.rejected || 0}</Typography>
                                    <Typography variant="body2" color="text.secondary">Rejected</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{rejection.rejectionRate || '0.00'}%</Typography>
                                    <Typography variant="body2" color="text.secondary">Rejection Rate</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        {(rejection.reasons || []).length > 0 && (
                            <TableContainer sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Reason</TableCell>
                                            <TableCell align="right">Count</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rejection.reasons.map((r, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{r._id || 'Not specified'}</TableCell>
                                                <TableCell align="right">{r.count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

// A deployment with features['lis.analyticsOverview'] gets the turnaround
// overview instead of the staff / QC / rejection dashboard.
export default function LabAnalytics() {
    const overview = useSelector(state => !!state.hospitalConfig?.features?.['lis.analyticsOverview']);
    return overview ? <TurnaroundOverview /> : <LabAnalyticsDashboard />;
}
